/**
 * Maps Candid write-back audit rows into CustomTable row objects.
 *
 * @module mapCandidWritebacksToTableData
 */
import { IconCellVariant } from "../../../../../components/custom-table/tableCell/cellTypes/IconCell";
import { formatDate } from "../../../../../utils/formatDate";
import { capitalizeString } from "../../../../../utils/stringModifications";
import {
  CandidUploadStatusBadge,
  CandidUploadTableKeys,
  CandidUploadTableRow,
} from "../types/candidUploadTableRow";

import {
  CandidWritebackRecord,
  CandidWritebackStatus,
} from "../types/candidWriteback";

/** Display format for the Updated At column. */
const UPDATED_AT_DISPLAY_FORMAT = "MM/DD/YYYY, h:mm A";

/** Badge colors for each write-back terminal status. */
const STATUS_BADGE_STYLES: Record<
  CandidWritebackStatus,
  CandidUploadStatusBadge
> = {
  success: {
    text: "Success",
    color: "#005D49",
    bgColor: "#E6F3F0",
  },
  error: {
    text: "Error",
    color: "#CC0300",
    bgColor: "#FFE8E8",
  },
};

/**
 * Formats `created_at` for the Updated At column.
 *
 * @param createdAt - ISO-8601 timestamp from the write-back row
 * @returns Locale-style date string, or an empty string when invalid
 */
const formatUpdatedAt = (createdAt: string): string =>
  formatDate(createdAt, UPDATED_AT_DISPLAY_FORMAT);

/**
 * Builds the Status badge cell from a write-back terminal status.
 *
 * @param status - Success or error outcome
 * @returns Badge text and colors for the BADGE table cell type
 */
const toStatusBadge = (
  status: CandidWritebackStatus,
): CandidUploadStatusBadge => STATUS_BADGE_STYLES[status];

/**
 * Maps one write-back record onto a CustomTable row.
 *
 * @param record - Audit row from the write-back history API
 * @returns Row keyed by {@link CandidUploadTableKeys}
 */
const mapCandidWritebackToTableRow = (
  record: CandidWritebackRecord,
): CandidUploadTableRow => ({
  [CandidUploadTableKeys.UpdatedAt]: formatUpdatedAt(record.created_at),
  [CandidUploadTableKeys.Type]: capitalizeString(record.use_case),
  [CandidUploadTableKeys.Status]: toStatusBadge(record.status),
  [CandidUploadTableKeys.View]: {
    variant: IconCellVariant.ARROW_RIGHT,
    rowData: record,
  },
});

/**
 * Converts write-back records into table rows for the Candid Upload table.
 *
 * @param records - Write-back rows to display
 * @returns Table rows in the same order as `records`
 */
export const mapCandidWritebacksToTableData = (
  records: CandidWritebackRecord[],
): CandidUploadTableRow[] => records.map(mapCandidWritebackToTableRow);
